import { Alert, Box, Button, Chip, Divider, Skeleton, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { OpsReferralAuditEntry } from '../../../interfaces/referral';
import referralService from '../../../services/referral';
import { camelCaseToSentence } from '../../../util/stringFormat';
import { diffValues, formatAuditValue } from './auditDiff';

const ACTION_LABEL: Record<string, string> = {
  create: 'Created',
  update: 'Edited',
  discard: 'Discarded',
};

/** `telephone.cell.number` → `Telephone › Cell › Number`, for a person, not a parser. */
const pathLabel = (field: string, path: string) =>
  [field, ...path.split('.').filter(Boolean)]
    .map((part) => {
      if (/^\d+$/.test(part)) {
        return String(Number(part) + 1);
      }
      const words = camelCaseToSentence(part);
      return words.charAt(0).toUpperCase() + words.slice(1);
    })
    .join(' › ');

const Entry: React.FC<{ entry: OpsReferralAuditEntry }> = ({ entry }) => {
  const changes = entry.changes.flatMap(({ field, oldValue, newValue }) =>
    diffValues(oldValue, newValue).map((change) => ({ field, ...change })),
  );

  return (
    <Box sx={{ py: 1.5 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
        <Chip size='small' variant='outlined' label={ACTION_LABEL[entry.action] ?? entry.action} />
        <Typography variant='body2' fontWeight={500}>
          {entry.changedBy.name ?? (entry.changedBy.id ? `User ${entry.changedBy.id}` : 'System')}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {new Date(entry.timestamp).toLocaleString()}
        </Typography>
      </Box>
      {entry.action === 'create' ? (
        // A creation records the whole referral as one value; listing every field as
        // "(empty) → value" would bury the entries that show real corrections.
        <Typography variant='caption' color='text.secondary'>
          Referral recorded.
        </Typography>
      ) : changes.length === 0 ? (
        // The API leaves some fields out of the history (subscriber ID, diagnosis codes, and,
        // until leads-api #127 allow-lists the `type`/`number` inside each phone, telephone),
        // so an edit to only those has nothing to show here; it did not necessarily change
        // nothing.
        <Typography variant='caption' color='text.secondary'>
          No change to the fields shown in this history.
        </Typography>
      ) : (
        changes.map(({ field, path, from, to, change }, index) => (
          <Typography
            key={`${field}.${path}.${index}`}
            variant='body2'
            sx={{ wordBreak: 'break-word' }}
          >
            <strong>{pathLabel(field, path)}:</strong>{' '}
            {change === 'added'
              ? `added ${formatAuditValue(to)}`
              : change === 'removed'
              ? `removed ${formatAuditValue(from)}`
              : `${formatAuditValue(from)} → ${formatAuditValue(to)}`}
          </Typography>
        ))
      )}
    </Box>
  );
};

/**
 * Who changed this referral, what, and when — the brief's "changes to a referral field are
 * attributable and readable in the UI". Newest first, from leads-api's audit log.
 */
const ReferralHistory: React.FC<{ referralId: number; refreshKey?: unknown }> = ({
  referralId,
  refreshKey,
}) => {
  const [entries, setEntries] = useState<OpsReferralAuditEntry[] | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [failed, setFailed] = useState(false);

  // The latest request wins: a refresh after a save can overlap the first load, and the
  // older response must not replace the newer one or land after unmount. The page also keys
  // this component by referral, so another referral starts from an empty history.
  const request = useRef(0);
  useEffect(
    () => () => {
      request.current += 1;
    },
    [],
  );

  const load = useCallback(async () => {
    const current = ++request.current;
    setFailed(false);
    try {
      const history = await referralService.getOpsReferralAuditHistory(referralId);
      if (current === request.current) {
        setEntries(history.entries);
        setTruncated(Boolean(history.truncated));
      }
    } catch {
      if (current === request.current) {
        setEntries(null);
        setFailed(true);
      }
    }
  }, [referralId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (failed) {
    return (
      <Alert
        severity='error'
        action={
          <Button color='inherit' size='small' onClick={load}>
            Retry
          </Button>
        }
      >
        Could not load the change history.
      </Alert>
    );
  }

  if (!entries) {
    return <Skeleton variant='rectangular' height={160} />;
  }

  if (entries.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary'>
        No changes recorded for this referral yet.
      </Typography>
    );
  }

  return (
    <Box>
      {truncated && (
        // The API returns the newest 200. Said, so the oldest entry shown is not mistaken for
        // the referral's creation.
        <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 1 }}>
          Showing the 200 most recent changes.
        </Typography>
      )}
      {entries.map((entry, index) => (
        <React.Fragment key={entry.id}>
          {index > 0 && <Divider />}
          <Entry entry={entry} />
        </React.Fragment>
      ))}
    </Box>
  );
};

export default ReferralHistory;
